import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Check } from 'lucide-react'

import mitraCelebrate from '@/assets/mascot/mitra-celebrate.png'
import Reveal from '@/components/motion/Reveal'
import Stagger from '@/components/motion/Stagger'
import { buttonVariants } from '@/components/ui/button-variants'
import { useMitraEnabled } from '@/features/mitra/api/access'
import { useMitraStore } from '@/features/mitra/store'
import { useOnboarding } from '@/features/onboarding/api/queries'
import { PERSONAS } from '@/features/onboarding/personas'
import { PLAN_PRESENTATION } from '@/features/onboarding/plans'
import { useOnboardingStore } from '@/features/onboarding/store'
import { planQueryOptions } from '@/features/subscriptions/api/queries'
import { periodLabel } from '@/features/subscriptions/lib/catalog'
import { requireAuth } from '@/lib/auth/guards'
import Confetti from '@/screens/onboarding/components/Confetti'
import StepProgress from '@/screens/onboarding/components/StepProgress'

/**
 * Each card's own destination — every one used to route `to: '/'` regardless
 * of its label, which read as unfinished wiring rather than a placeholder
 * (the bug the 2026-09-28 refresh flagged). "Ask Mitra" has no route of its
 * own: opening the panel is a store action (`openMitra`), fired on click
 * before the same Home navigation the other two use routes for.
 */
const NEXT_STEPS = [
  {
    title: 'See what moved today',
    body: 'The market page opens on the session you just missed.',
    cta: 'Open Market',
    to: '/market' as const,
    openMitra: false,
  },
  {
    title: 'Add what you hold',
    body: 'Your book, dividends and returns in one place.',
    cta: 'Build portfolio',
    to: '/portfolio' as const,
    openMitra: false,
  },
  {
    title: 'Ask Mitra anything',
    body: 'Try “why did NABIL fall?” and see the plain answer.',
    cta: 'Ask Mitra',
    to: '/' as const,
    openMitra: true,
  },
] as const

export const Route = createFileRoute('/onboarding/done')({
  beforeLoad: requireAuth,
  component: AllDoneScreen,
})

function AllDoneScreen() {
  const { data: onboarding } = useOnboarding()
  const choice = useOnboardingStore((s) => s.choice)
  const openMitraPanel = useMitraStore((s) => s.openPanel)
  // "Ask Mitra anything" is a launcher: left out for a reader Mitra is not open to.
  const mitraEnabled = useMitraEnabled()
  const nextSteps = NEXT_STEPS.filter((s) => mitraEnabled || !s.openMitra)

  // Free's period is never drawn (see `period` below), so there is nothing
  // this fetch would feed for a Free choice or a member who jumped straight
  // to this URL with nothing chosen — no request fires for either.
  const fetchPeriod = choice !== null && choice.tier !== 'free'
  const planDetail = useQuery({
    ...planQueryOptions(choice?.planId ?? 0),
    enabled: fetchPeriod,
  })

  // `onboarding` is undefined while the fetch is in flight and `persona` is
  // null for a member who skipped the picker — both read the same here,
  // since either way this screen has no persona it is entitled to claim.
  const persona = onboarding?.persona
    ? PERSONAS.find((p) => p.id === onboarding.persona)
    : undefined
  const planName = choice === null ? null : PLAN_PRESENTATION[choice.tier].name
  // Free's `periodDays` is the registration row's ~10-year span, not a real
  // billing cycle — never drawn as "9999 days". A paid choice shows it only
  // once its own detail has loaded; until then the line drops the period
  // rather than guessing one.
  const period =
    choice !== null && choice.tier !== 'free' && planDetail.data
      ? periodLabel(planDetail.data.plan.periodDays)
      : null

  // Reads back exactly what the user chose. Skipping a step drops its line
  // rather than inventing an answer they never gave.
  const summary = [
    // The role verbatim. Figma's Maya frame reads "Just starting out, learning
    // the ground", but that tail is Maya's copy — pinned to Sita it produced
    // "Already holding, learning the ground", which contradicts itself.
    persona && { title: `You start as ${persona.name}`, body: persona.role },
    // The period is the only second line a plan can honestly add — what it
    // INCLUDES is its feature flags' to say, not this screen's.
    planName !== null && { title: `${planName} plan`, body: period },
    { title: 'First objective pinned', body: 'Learn how the share market works' },
  ].filter((s): s is { title: string; body: string | null } => Boolean(s))

  const paidChoice = choice !== null && choice.tier !== 'free' ? choice : null

  return (
    <Reveal variant="fade-in" className="bg-background text-foreground min-h-dvh px-8 py-8">
      <Stagger className="mx-auto max-w-300">
        <StepProgress current={3} align="start" />

        <div className="mt-16 grid animate-none items-center gap-12 lg:grid-cols-2">
          <Stagger as="section" className="animate-none">
            <p className="text-accent text-label-s font-semibold">All done</p>
            <h1 className="mt-4 text-display-l">Welcome to MoneyMitra.</h1>

            <ul className="mt-8 space-y-4">
              {summary.map((s) => (
                <li key={s.title} className="flex gap-3">
                  <span className="bg-tone-accent-soft grid size-6 shrink-0 place-items-center rounded-full">
                    <Check className="text-success size-4" aria-hidden />
                  </span>
                  <div>
                    <p className="text-h-s">{s.title}</p>
                    {s.body !== null && (
                      <p className="text-muted-foreground text-body-s">{s.body}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {paidChoice === null ? (
              <Link to="/" className={`mt-9 h-12 text-h-m ${buttonVariants({ size: 'hero' })}`}>
                Take me home
              </Link>
            ) : (
              <div className="mt-9 flex flex-wrap gap-3">
                <Link
                  to="/subscription/checkout"
                  search={{ plan: paidChoice.planId }}
                  className={buttonVariants({ size: 'hero' })}
                >
                  Continue to payment
                </Link>
                <Link to="/" className={buttonVariants({ variant: 'secondary', size: 'hero' })}>
                  Start on Free
                </Link>
              </div>
            )}
          </Stagger>

          <Stagger.Item as="section" index={2} className="relative hidden justify-center lg:flex">
            <Confetti />
            {/* Token-driven glow, not a retired-palette SVG (the 2026-09-28
                refresh's `hero-glow.svg` baked in the old #32e36a accent at
                3 gradient stops) — a plain radial gradient off the live
                `--accent-base` token instead, so a future retune carries
                through with no asset re-export. */}
            <div className="bg-radial glow-breathe size-105 rounded-full from-accent/28 to-transparent" />
            {/* rest:ub — Mitra jumps once as the page lands (mock `ob-jump`, .9s) and the glow
                breathes behind (`ob-breathe`, 5s). Both are off under reduced motion. */}
            <img
              src={mitraCelebrate}
              alt="Mitra celebrating"
              className="mitra-jump absolute top-1/2 left-1/2 h-85 w-auto -translate-x-1/2 -translate-y-1/2"
            />
          </Stagger.Item>
        </div>

        {/* rest:ub (COMPARE-au §8) — each card is ONE link. Hover draws an inset 1px hairline and lifts
            the card 2px (hairline over the fast step, lift over the med step on the out curve); the
            gold word at its foot underlines with the card; keyboard focus is the kit's white ring. */}
        <ul className="mt-16 grid gap-6 pb-16 md:grid-cols-3">
          {nextSteps.map((s) => (
            <li key={s.title}>
              <Link
                to={s.to}
                onClick={
                  s.openMitra
                    ? () => {
                        openMitraPanel()
                      }
                    : undefined
                }
                className="group bg-popover block h-full p-5 shadow-[inset_0_0_0_1px_transparent] transition-[box-shadow,translate] duration-fast hover:shadow-[inset_0_0_0_1px_var(--border)] focus-visible:shadow-[inset_0_0_0_1px_var(--border)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control motion-safe:hover:-translate-y-(--motion-nudge) motion-safe:hover:duration-med motion-safe:hover:ease-out"
              >
                <h2 className="text-h-m">{s.title}</h2>
                <p className="text-muted-foreground mt-2 text-body-m">{s.body}</p>
                <span className="text-accent mt-3 inline-block text-body-xs font-semibold underline-offset-3 group-hover:underline group-focus-visible:underline">
                  {s.cta}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Stagger>
    </Reveal>
  )
}
